
import React, { useEffect, useRef } from 'react';
import { InventoryItem, LabelSize } from '../types'; // Added LabelSize
import { DEFAULT_LABEL_SIZE_ID, DYMO_LABEL_SIZES } from '../constants'; // Added DEFAULT_LABEL_SIZE_ID

// Note: JsBarcode is loaded via CDN in index.html
declare var JsBarcode: any;

interface PrintableInventoryLabelProps {
  item: InventoryItem;
  labelSizeId?: string; // Optional: if not provided, uses default
}

const PREVIEW_DPI = 96; // Standard screen DPI

export const PrintableInventoryLabel: React.FC<PrintableInventoryLabelProps> = ({ item, labelSizeId = DEFAULT_LABEL_SIZE_ID }) => {
  const barcodeRef = useRef<SVGSVGElement>(null);
  const selectedLabelSize = DYMO_LABEL_SIZES.find(size => size.id === labelSizeId) || DYMO_LABEL_SIZES[0];
  const { widthInches, heightInches } = selectedLabelSize;

  useEffect(() => {
    if (item && barcodeRef.current) {
      const barcodeData = item.sku || item.id;
      try {
        JsBarcode(barcodeRef.current, barcodeData, {
          format: "CODE128",
          width: 1.5,
          height: Math.min(40, heightInches * PREVIEW_DPI * 0.3),
          displayValue: true,
          fontSize: 8,
          textMargin: 0,
          margin: 2,
        });
      } catch (e) {
        console.error("Barcode generation failed for inventory label:", e);
         if (barcodeRef.current) {
            barcodeRef.current.innerHTML = '<text x="10" y="20" fill="red" font-size="8">Error</text>';
        }
      }
    }
  }, [item, widthInches, heightInches]);

  if (!item) return null;

  const labelStyle: React.CSSProperties = {
    width: `${widthInches * PREVIEW_DPI}px`,
    height: `${heightInches * PREVIEW_DPI}px`,
    fontSize: '8pt',
    border: '1px dashed #9ca3af',
    padding: '0.1in',
    boxSizing: 'border-box',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    color: 'black',
    lineHeight: '1.2',
  };

  const maxBarcodeHeight = heightInches * PREVIEW_DPI * 0.4;

  return (
    <div style={labelStyle} className="font-sans">
      <div>
        <h1 style={{ fontSize: Math.min(9, widthInches * 2.8) + 'pt' }} className="font-bold truncate mb-1" title={item.name}>{item.name}</h1>
        <div className="grid grid-cols-2 gap-x-1 items-start">
          <div className="overflow-hidden">
            <p className="font-semibold">SKU: <span className="font-normal">{item.sku || 'N/A'}</span></p>
            {item.category && <p className="font-semibold truncate">Cat: <span className="font-normal">{item.category}</span></p>}
            {typeof item.price === 'number' && item.source === 'square' && <p style={{ fontSize: Math.min(10, widthInches * 3) + 'pt' }} className="font-bold mt-0.5">Price: ${item.price.toFixed(2)}</p>}
            {typeof item.cost === 'number' && item.source === 'service' && <p style={{ fontSize: Math.min(10, widthInches * 3) + 'pt' }} className="font-bold mt-0.5">Cost: ${item.cost.toFixed(2)}</p>}
          </div>
           <div className="flex justify-center items-center" style={{ height: `${maxBarcodeHeight}px` }}>
            <svg ref={barcodeRef} className="max-w-full max-h-full"></svg>
          </div>
        </div>
      </div>
      <div className="text-right mt-auto">
        <p style={{ fontSize: '7pt' }}>ID: {item.id} ({item.source})</p>
      </div>
    </div>
  );
};
